/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { apiUserBaseQuery } from './apiSlice';

/** One row of the finance dashboard. Deliberately carries no document number. */
export type KycVerifiedWorker = {
  worker_id: string;
  name: string | null;
  gender: string | null;
  yob: number | null;
  kyc_completed_at: string | null;
};

export type KycVerifiedWorkersResponse = {
  workers: KycVerifiedWorker[];
  total: number;
  limit: number;
  offset: number;
};

export type KycVerifiedWorkersRequest = {
  limit: number;
  offset: number;
  verifiedFrom?: string;
  verifiedTo?: string;
};

export type KycPanReportRequest = {
  reason: string;
  verifiedFrom?: string;
  verifiedTo?: string;
};

/** Shape returned by the export endpoint: the password-encrypted xlsx body plus the filename the server chose. */
export type KycPanReportDownload = {
  blob: Blob;
  filename: string;
};

/** The `messages[0]` of a backend GenericError carried by a failed RTK Query call, if any. */
export function getApiErrorMessage(error: unknown): string | undefined {
  const messages = (error as { data?: { messages?: unknown } } | null)?.data?.messages;
  return Array.isArray(messages) && typeof messages[0] === 'string' ? messages[0] : undefined;
}

function toQueryParams(request: { verifiedFrom?: string; verifiedTo?: string }): Record<string, string> {
  return {
    ...(request.verifiedFrom ? { verified_from: request.verifiedFrom } : {}),
    ...(request.verifiedTo ? { verified_to: request.verifiedTo } : {}),
  };
}

export const kycFinanceApi = createApi({
  reducerPath: 'kycFinanceApi',
  baseQuery: apiUserBaseQuery,
  tagTypes: ['KycVerifiedWorkers'],
  endpoints: (builder) => ({
    getKycVerifiedWorkers: builder.query<KycVerifiedWorkersResponse, KycVerifiedWorkersRequest>({
      query: (request) => ({
        url: '/kyc_finance/workers',
        params: { limit: String(request.limit), offset: String(request.offset), ...toQueryParams(request) },
      }),
      providesTags: ['KycVerifiedWorkers'],
    }),

    /**
     * Downloads the decrypted PAN report.
     *
     * A mutation rather than a query so it is never cached or auto-refetched — a decrypted PAN
     * export must happen exactly when the user asks for it, once per audited request. Routed through
     * `apiUserBaseQuery` so the `project_id` header and session cookie are attached; a plain anchor
     * navigation cannot set that header, which is why this is not a simple download link.
     */
    downloadKycPanReport: builder.mutation<KycPanReportDownload, KycPanReportRequest>({
      query: (request) => ({
        url: '/kyc_finance/workers/export',
        params: { reason: request.reason, ...toQueryParams(request) },
        responseHandler: async (response) => {
          // fetchBaseQuery runs this before its status check, so an error body (a JSON GenericError)
          // must be passed through as-is or it would be wrapped into a "successful" blob download.
          if (!response.ok) {
            return response.json().catch(() => null);
          }

          const blob = await response.blob();
          const disposition = response.headers.get('content-disposition') ?? '';
          const match = /filename="?([^"]+)"?/.exec(disposition);
          return { blob, filename: match?.[1] ?? 'kyc-pan-report.xlsx' };
        },
      }),
    }),
  }),
});

export const { useGetKycVerifiedWorkersQuery, useDownloadKycPanReportMutation } = kycFinanceApi;
